import {
  Center,
  Text,
  Paper,
  Avatar,
  Group,
  Tabs,
  Box,
} from '@mantine/core';
import { useParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
import { getJson } from '@/services/httpClient';
import { MarketplaceTabLabel } from '@/components/MarketplaceTabLabel';
import {
  IconStar,
  IconPackage,
  IconTemplate,
  IconEye,
} from '@tabler/icons-react';
import { userPath, cdnCompanyLogos, layoutPath } from '../ApiPaths';
import { pageWrapper } from '@/Styles';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';
import { PageHeader } from '@/components/PageHeader';
import marketplaceTabStyles from '@/components/MarketplaceTabs.module.css';
import { getMarketplaceTabStyleVars } from '@/components/marketplaceTabStyleVars';
import { UserProfile, useStore } from '@/store/Store';
import { IProduct } from '@/types/Product.model';
import { ITable } from '@/types/Table';
import { IPopulatedProduct, ProductLayoutGallery } from './Home.page';
import type { ProductStarState } from '@/hooks/useProductStarToggle';
// import { FrameGallery } from '@/components/FrameGallery';
// import { Canvas } from '@react-three/fiber';
import { useTranslation } from 'react-i18next';
import { getListingsByProductId, getProductsByUser, getStarredProductsByUserId } from '@/Api';
import { IPopulatedListing } from '@/types/Listing.model';
import classes from './UserViewer.module.css';

const getProducts = (
  id: string,
  onSuccess: (products: Partial<IProduct>[]) => void,
  onError: (err: any) => void
) => getProductsByUser(id).then(onSuccess).catch(onError);

const getLayouts = (
  id: string,
  onSuccess: (layouts: Partial<IProduct>[]) => void,
  onError: (err: any) => void
) => {
  getJson<Partial<IProduct>[]>(`${layoutPath}byUser/${id}`)
    .then((data) => onSuccess(data))
    .catch((error) => onError(error));
};

// Interface for products with listings (similar to Dashboard)
interface ProductWithListings {
  _id?: string;
  productName?: string;
  productPage?: string;
  owningUser_id?: string | Partial<UserProfile>;
  productImage?: string;
  views?: number;
  availableFormats?: string[];
  layout?: string;
  modelViewerSettings?: string;
  isPublic?: boolean;
  articleNumber?: string | null;
  metadata?: ITable;
  listings?: IPopulatedListing[];
  starredByMe?: boolean;
  starCount?: number;
}

async function buildViewerStarredIdSet(
  profileUserId: string,
  profileStarredProducts: Partial<IProduct>[],
  viewerUserId?: string
): Promise<Set<string>> {
  if (!viewerUserId) return new Set();

  if (viewerUserId === profileUserId) {
    return new Set(
      profileStarredProducts.map((product) => String(product._id)).filter(Boolean)
    );
  }

  const viewerStarred = await getStarredProductsByUserId(viewerUserId);
  return new Set(viewerStarred.map((product) => String(product._id)).filter(Boolean));
}

function withViewerStarState(
  products: Partial<IProduct>[],
  viewerStarredIds: Set<string>
): Partial<IProduct>[] {
  return products.map((product) => ({
    ...product,
    starredByMe: product._id ? viewerStarredIds.has(String(product._id)) : false,
  }));
}

function populateProducts(
  products: Partial<IProduct>[],
  user: Partial<UserProfile>
): IPopulatedProduct[] {
  return products.map((product) => {
    const { owningUser_id, ...rest } = product;
    return {
      ...rest,
      owningUser_id: user,
    };
  });
}

function isPopulatedUserProfile(
  value: string | Partial<UserProfile> | undefined
): value is Partial<UserProfile> {
  return (
    typeof value === 'object' &&
    value !== null &&
    (typeof value.companyName === 'string' ||
      typeof value.username === 'string' ||
      typeof value.companyLogo === 'string')
  );
}

function resolveProfileProductOwner(
  product: Partial<IProduct>,
  profileUser: Partial<UserProfile>
): Partial<UserProfile> {
  if (isPopulatedUserProfile(product.owningUser_id)) {
    return product.owningUser_id;
  }
  return profileUser;
}

// Function to populate products with listings (similar to Dashboard)
async function populateProductsWithListings(
  products: Partial<IProduct>[],
  user: Partial<UserProfile>
): Promise<ProductWithListings[]> {
  const productsWithListings = await Promise.all(
    products.map(async (product: Partial<IProduct>) => {
      const owningUser_id = resolveProfileProductOwner(product, user);
      if (product._id) {
        try {
          const listings = await getListingsByProductId(product._id);
          return { ...product, listings, owningUser_id };
        } catch (error) {
          console.error(`Error fetching listings for product ${product._id}:`, error);
          return { ...product, listings: [], owningUser_id };
        }
      }
      return { ...product, listings: [], owningUser_id };
    })
  );

  return productsWithListings;
}

// Temporary solution - ProductLayoutGallery wrapper
const EnhancedProductLayoutGallery = ({
  layoutList,
  isLayout,
  onStarChange,
}: {
  layoutList: ProductWithListings[];
  isLayout: boolean;
  onStarChange?: (productId: string, state: ProductStarState) => void;
}) => {
  return (
    <ProductLayoutGallery
      layoutList={layoutList}
      isLayout={isLayout}
      onStarChange={onStarChange}
    />
  );
};

export function UserViewer() {
  const { t } = useTranslation();
  const { username } = useParams();
  const session = useSessionContext();
  const hasSession =
    !session.loading &&
    typeof session === 'object' &&
    'doesSessionExist' in session &&
    session.doesSessionExist;
  const viewerProfile = useStore((state) => state.userProfile);
  const [userData, setUserData] = useState<UserProfile>();
  const [myProducts, setMyProducts] = useState<ProductWithListings[]>([]);
  const [myLayouts, setMyLayouts] = useState<IPopulatedProduct[]>([]);
  const [starredProducts, setStarredProducts] = useState<ProductWithListings[]>([]);
  const [activeTab, setActiveTab] = useState<string | null>('products');
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    getJson<UserProfile>(userPath + 'byusername/' + username)
      .then((data) => setUserData(data))
      .catch((error) => console.error(error));
  }, []);

  useEffect(() => {
    if (!userData || !userData._id) return;

    // Fetch products with listings (similar to Dashboard)
    getProducts(
      userData._id,
      async (productList) => {
        try {
          const populatedProducts = await populateProductsWithListings(productList, userData);
          setMyProducts(populatedProducts);
        } catch (error) {
          console.error('Error populating products with listings:', error);
          // Fallback to products without listings
          const populatedProducts = populateProducts(productList, userData);
          setMyProducts(populatedProducts as ProductWithListings[]);
        }
      },
      console.error
    );

    getLayouts(
      userData._id,
      (layoutList) => {
        const populatedLayouts = populateProducts(layoutList, userData);
        setMyLayouts(populatedLayouts);
      },
      console.error
    );
  }, [userData]);

  const isOwnProfile = useMemo(
    () =>
      Boolean(
        viewerProfile._id &&
          userData?._id &&
          String(viewerProfile._id) === String(userData._id)
      ),
    [viewerProfile._id, userData?._id]
  );

  const loadStarredProducts = useCallback(async () => {
    if (!userData?._id) return;
    if (session.loading) return;
    if (hasSession && !viewerProfile._id) return;

    try {
      const raw = (await getStarredProductsByUserId(userData._id)) as Partial<IProduct>[];
      const viewerUserId = hasSession ? viewerProfile._id : undefined;
      const viewerStarredIds = await buildViewerStarredIdSet(userData._id, raw, viewerUserId);
      const starredWithViewerState = withViewerStarState(raw, viewerStarredIds);
      const populated = await populateProductsWithListings(starredWithViewerState, userData);
      setStarredProducts(populated);
    } catch (error) {
      console.error('Error loading starred products:', error);
    }
  }, [userData?._id, session.loading, hasSession, viewerProfile._id]);

  useEffect(() => {
    void loadStarredProducts();
  }, [loadStarredProducts]);

  useEffect(() => {
    if (activeTab === 'starred') {
      void loadStarredProducts();
    }
  }, [activeTab, loadStarredProducts]);

  const handleProfileStarChange = useCallback(
    (productId: string, state: ProductStarState) => {
      setMyProducts((prev) =>
        prev.map((product) =>
          product._id === productId ? { ...product, ...state } : product
        )
      );

      if (!isOwnProfile) return;

      setStarredProducts((prev) => {
        if (!state.starredByMe) {
          return prev.filter((product) => product._id !== productId);
        }
        if (prev.some((product) => product._id === productId)) {
          return prev.map((product) =>
            product._id === productId ? { ...product, ...state } : product
          );
        }
        return prev;
      });

      void loadStarredProducts();
    },
    [isOwnProfile, loadStarredProducts]
  );

  const mainColor = userData?.qrCodeColor || DEFAULT_QR_CODE_COLOR;

  const { totalViews, totalStars } = useMemo(() => {
    let views = 0;
    let stars = 0;
    for (const product of myProducts) {
      views += product.views ?? 0;
      stars += product.starCount ?? 0;
    }
    return { totalViews: views, totalStars: stars };
  }, [myProducts]);

  return (
    <Center style={pageWrapper}>
      <Box
        style={{
          width: '80%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 'calc(100vh - 120px)', // Fixed page height
        }}
      >
        {/* User Profile Header */}
        <Paper
          shadow="xs"
          radius="lg"
          p="lg"
          withBorder
          mb="xl"
          className={classes.profileHero}
          style={{ '--profile-accent': mainColor } as CSSProperties}
        >
          <div className={classes.profileRow}>
            <PageHeader
              leading={
                <Avatar
                  size={72}
                  src={userData?.companyLogo ? `${cdnCompanyLogos}${userData.companyLogo}` : null}
                  color={mainColor}
                  radius="xl"
                >
                  {!userData?.companyLogo ? username?.charAt(0).toUpperCase() : null}
                </Avatar>
              }
              title={username}
              subtitle={userData?.companyName}
              accentColor={mainColor}
              mb={0}
            />

            {myProducts.length > 0 || myLayouts.length > 0 ? (
              <div className={classes.stats}>
                <div className={classes.statCard}>
                  <span className={classes.statIcon} aria-hidden>
                    <IconPackage size={18} stroke={1.75} />
                  </span>
                  <span className={classes.statMeta}>
                    <span className={classes.statValue}>{myProducts.length}</span>
                    <span className={classes.statLabel}>{t('userProfile.products')}</span>
                  </span>
                </div>
                <div className={classes.statCard}>
                  <span className={classes.statIcon} aria-hidden>
                    <IconTemplate size={18} stroke={1.75} />
                  </span>
                  <span className={classes.statMeta}>
                    <span className={classes.statValue}>{myLayouts.length}</span>
                    <span className={classes.statLabel}>{t('userProfile.templates')}</span>
                  </span>
                </div>
                <div className={classes.statCard}>
                  <span className={classes.statIcon} aria-hidden>
                    <IconEye size={18} stroke={1.75} />
                  </span>
                  <span className={classes.statMeta}>
                    <span className={classes.statValue}>{totalViews}</span>
                    <span className={classes.statLabel}>{t('userProfile.totalViews')}</span>
                  </span>
                </div>
                <div className={classes.statCard}>
                  <span className={classes.statIcon} aria-hidden>
                    <IconStar size={18} stroke={1.75} />
                  </span>
                  <span className={classes.statMeta}>
                    <span className={classes.statValue}>{totalStars}</span>
                    <span className={classes.statLabel}>{t('userProfile.totalStars')}</span>
                  </span>
                </div>
              </div>
            ) : null}
          </div>
        </Paper>

        {/* 3D Viewer - Keep original */}
        {/* Temporarily disabled 3D viewer
        {myProducts.length > 0 && (
          <Box mb="xl">
            <Canvas
              style={{
                width: '100%',
                height: '400px',
                borderRadius: '8px',
                boxShadow: '0 3px 10px rgba(0,0,0,0.08)',
              }}
              shadows
              camera={{ position: [0, 0, 10], fov: 30 }}
            >
              <FrameGallery productList={myProducts} />
            </Canvas>
          </Box>
        )}
        */}

        {/* Products & Templates Tabs */}
        <Paper shadow="xs" radius="lg" p="md" mb="xl">
          <Tabs
            value={activeTab}
            onChange={setActiveTab}
            variant="pills"
            radius="md"
            style={getMarketplaceTabStyleVars(mainColor)}
            classNames={{ list: marketplaceTabStyles.list }}
            styles={{ tabLabel: { width: '100%' } }}
          >
            <Group justify="space-between" align="flex-start" mb={10} w="100%" wrap="wrap">
              <Tabs.List style={{ flexWrap: 'wrap', marginBottom: '10px' }}>
                <Tabs.Tab
                  value="products"
                  classNames={{ tab: marketplaceTabStyles.tab }}
                >
                  <MarketplaceTabLabel
                    icon={<IconPackage size={16} stroke={1.75} />}
                    label={t('userProfile.products')}
                    count={myProducts.length}
                    hideLabel={isMobile}
                  />
                </Tabs.Tab>
                <Tabs.Tab
                  value="templates"
                  classNames={{ tab: marketplaceTabStyles.tab }}
                >
                  <MarketplaceTabLabel
                    icon={<IconTemplate size={16} stroke={1.75} />}
                    label={t('userProfile.templates')}
                    count={myLayouts.length}
                    hideLabel={isMobile}
                  />
                </Tabs.Tab>
                <Tabs.Tab
                  value="starred"
                  classNames={{ tab: marketplaceTabStyles.tab }}
                >
                  <MarketplaceTabLabel
                    icon={<IconStar size={16} stroke={1.75} />}
                    label={t('userProfile.starred')}
                    count={starredProducts.length}
                    hideLabel={isMobile}
                  />
                </Tabs.Tab>
              </Tabs.List>
            </Group>

            <Tabs.Panel value="products" pt="md">
              {myProducts.length > 0 ? (
                <Box>
                  <Text mb={20} color="dimmed" size="sm">
                    {t('userProfile.productsDescription')}
                  </Text>
                  <EnhancedProductLayoutGallery
                    layoutList={myProducts}
                    isLayout={false}
                    onStarChange={handleProfileStarChange}
                  />
                </Box>
              ) : (
                <Text c="dimmed" ta="center" py="xl">
                  {t('userProfile.noProducts')}
                </Text>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="templates" pt="md">
              {myLayouts.length > 0 ? (
                <Box>
                  <Text mb={20} color="dimmed" size="sm">
                    {t('userProfile.templatesDescription')}
                  </Text>
                  <EnhancedProductLayoutGallery
                    layoutList={myLayouts}
                    isLayout={true}
                  />
                </Box>
              ) : (
                <Text c="dimmed" ta="center" py="xl">
                  {t('userProfile.noTemplates')}
                </Text>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="starred" pt="md">
              {starredProducts.length > 0 ? (
                <Box>
                  <Text mb={20} color="dimmed" size="sm">
                    {t('userProfile.starredDescription')}
                  </Text>
                  <EnhancedProductLayoutGallery
                    layoutList={starredProducts}
                    isLayout={false}
                    onStarChange={handleProfileStarChange}
                  />
                </Box>
              ) : (
                <Text c="dimmed" ta="center" py="xl">
                  {t('userProfile.noStarred')}
                </Text>
              )}
            </Tabs.Panel>
          </Tabs>
        </Paper>
      </Box>
    </Center>
  );
}
